import { z } from "zod";

import { SHAREABLE_OBJECT_TYPES } from "../schemas/contractObjectShare";

/**
 * Kanonik domain olay kataloğu (CLOS-324 · parent CLOS-280).
 *
 * **Tek kaynak budur.** Üretici de tüketici de olayı buradan tanır; serbest metin olay
 * türü yoktur. Bir olayın adı iki yerde ayrı ayrı yazılabilseydi, gün gelir "aynı iş
 * olayı" için iki ad doğar ve tüketiciler ikiye bölünürdü — biri `contract.signed`,
 * diğeri `contract.imzalandi` dinler ve arada bir olay kaybolur.
 *
 * **Katalogda YALNIZ bugün gerçekten olan durum değişiklikleri var.** Henüz kartı
 * olmayan bir domain için "ileride lazım olur" diye olay tanımlanmaz (ADR-0014); domain
 * kartı geldiğinde kendi satırını ekler ve kilitli ad testini bilerek günceller.
 *
 * Yönlendirme (hangi olay Bildirim/Bekleyen İşlem/Aktivite/Audit üretir) BU DOSYADA
 * DEĞİLDİR — o bir ürün kararıdır ve CLOS-323'ün matrisine aittir. Adlandırma ile
 * yönlendirmeyi aynı yere koymak, teknik bir değişikliği ürün kararına dokunmadan
 * yapmayı imkânsız kılardı.
 */

/** Olayın gerektirdiği aktör türü. **AI yoktur** — AI aktör değil kaynaktır (CLOS-318). */
export type EventActorRequirement = "user" | "system";

/**
 * Payload'ın taşıdığı en hassas veri sınıfı. Saklama ve yönlendirme kararları buna
 * bakar; sınıfı olmayan bir olay, "ne kadar dikkat edileceği" sorusunu cevapsız bırakır.
 */
export type EventSensitivity = "operational" | "personal" | "confidential";

export type EventDefinition = {
  name: string;
  /** Payload şemasının sürümü. Kırıcı değişiklik yeni sürüm ister (bkz. `docs/olay-katalogu.md`). */
  version: number;
  /** Olayı üreten modül — "bu olayı kim değiştirebilir?" sorusunun cevabı. */
  producer: string;
  actor: EventActorRequirement;
  sensitivity: EventSensitivity;
  /** Şema `strict`: bilinmeyen alan reddedilir. */
  payload: z.ZodType;
  summary: string;
};

/**
 * Payload şemaları `tenantId`/`actorId` TANIMLAMAZ — bunlar zarfın alanlarıdır.
 *
 * Payload'da da bulunsalardı iki gerçek kaynağı olurdu ve bir üretici zarftakinden
 * farklı bir tenant yazabilirdi; tüketici hangisine inanacağını bilemezdi.
 */
export const EVENT_CATALOG = {
  "tenant.created": {
    name: "tenant.created",
    version: 1,
    producer: "tenants",
    actor: "user",
    sensitivity: "operational",
    summary: "Yeni firma oluşturuldu.",
    payload: z.strictObject({ name: z.string().min(1) }),
  },
  "membership.created": {
    name: "membership.created",
    version: 1,
    producer: "tenants",
    actor: "user",
    sensitivity: "personal",
    summary: "Firmaya üye eklendi.",
    payload: z.strictObject({
      membershipId: z.uuid(),
      memberUserId: z.uuid(),
      role: z.string().min(1),
    }),
  },
  /**
   * ### Organizasyon domaini yaşam döngüsü olayları (CLOS-283 `[055]`)
   *
   * ⚠ Kart: *"yalnız ... ANLAMLI yaşam döngüsü olayları projection üretir; her okuma veya
   * teknik güncelleme aktivite sayılmaz."* Bu yüzden burada okuma, liste, sayfalama ya da
   * salt teknik güncelleme olayı YOK. Ölçüt tek soru: *"altı ay sonra biri 'bu ne zaman,
   * kim tarafından oldu?' diye sorar mı?"*
   *
   * ⚠ Payload'lar KİMLİK taşıyor, KİŞİSEL VERİ taşımıyor: olay, hedefi görme yetkisi
   * olmayan tüketicilere de gidiyor ve adı/e-postası payload'a girseydi yetki kapısının
   * arkasından dolaşırdı. Görünen ad okuma yolunda, yetkiyle çözülüyor.
   */
  "organization.profile_updated": {
    name: "organization.profile_updated",
    version: 1,
    producer: "tenants",
    actor: "user",
    sensitivity: "operational",
    summary: "Organizasyon profili güncellendi.",
    /** Alan ADLARI var, DEĞERLER yok: vergi numarası bir olay payload'ında dolaşmamalı. */
    payload: z.strictObject({ fields: z.array(z.string().min(1)).min(1) }),
  },
  "organization.closed": {
    name: "organization.closed",
    version: 1,
    producer: "tenants",
    actor: "user",
    sensitivity: "operational",
    summary: "Organizasyon operasyonel olarak kapatıldı.",
    /** Gerekçe KODU; serbest metin not payload'a girmiyor (kullanıcı yazdığı için). */
    payload: z.strictObject({ reason: z.string().min(1) }),
  },
  "organization.reactivated": {
    name: "organization.reactivated",
    version: 1,
    producer: "tenants",
    actor: "user",
    sensitivity: "operational",
    summary: "Organizasyon yeniden etkinleştirildi.",
    payload: z.strictObject({}),
  },
  "membership.status_changed": {
    name: "membership.status_changed",
    version: 1,
    producer: "tenants",
    actor: "user",
    sensitivity: "personal",
    summary: "Üyelik durumu değişti.",
    payload: z.strictObject({
      membershipId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
      reason: z.string().min(1),
    }),
  },
  "membership.role_changed": {
    name: "membership.role_changed",
    version: 1,
    producer: "tenants",
    actor: "user",
    sensitivity: "personal",
    summary: "Üyeliğin rolü değişti.",
    payload: z.strictObject({ membershipId: z.uuid(), roleId: z.uuid() }),
  },
  "invitation.created": {
    name: "invitation.created",
    version: 1,
    producer: "tenants",
    actor: "user",
    sensitivity: "personal",
    summary: "Organizasyona davet gönderildi.",
    /** E-POSTA YOK: davetlinin adresi, daveti görme yetkisi olmayanlara sızmamalı. */
    payload: z.strictObject({ invitationId: z.uuid(), roleId: z.uuid() }),
  },
  "invitation.resolved": {
    name: "invitation.resolved",
    version: 1,
    producer: "tenants",
    actor: "user",
    sensitivity: "personal",
    /**
     * Dört sonuç TEK olayda: kabul, ret, iptal ve süre sonu aynı yaşam döngüsünün
     * kapanışı ve hepsinin tüketicisi aynı. Dört ayrı olay olsaydı yönlendirme matrisi,
     * projector kuralı ve özet metni dört kez yazılır, biri gün gelip ayrışırdı.
     */
    summary: "Davet sonuçlandı.",
    payload: z.strictObject({
      invitationId: z.uuid(),
      outcome: z.enum(["accepted", "rejected", "cancelled", "expired"]),
    }),
  },
  "department.created": {
    name: "department.created",
    version: 1,
    producer: "departments",
    actor: "user",
    sensitivity: "operational",
    summary: "Departman oluşturuldu.",
    payload: z.strictObject({ departmentId: z.uuid(), name: z.string().min(1) }),
  },
  "department.archived": {
    name: "department.archived",
    version: 1,
    producer: "departments",
    actor: "user",
    sensitivity: "operational",
    summary: "Departman arşivlendi.",
    payload: z.strictObject({ departmentId: z.uuid() }),
  },
  "department.reactivated": {
    name: "department.reactivated",
    version: 1,
    producer: "departments",
    actor: "user",
    sensitivity: "operational",
    summary: "Departman yeniden aktifleştirildi.",
    payload: z.strictObject({ departmentId: z.uuid() }),
  },
  "role.created": {
    name: "role.created",
    version: 1,
    producer: "authorization",
    actor: "user",
    sensitivity: "operational",
    summary: "Özel rol oluşturuldu.",
    payload: z.strictObject({ roleId: z.uuid(), name: z.string().min(1) }),
  },
  "role.permissions_changed": {
    name: "role.permissions_changed",
    version: 1,
    producer: "authorization",
    actor: "user",
    sensitivity: "operational",
    /**
     * ⚠ Yetki ANAHTARLARI payload'da: rolün ne kazandığı/kaybettiği güvenlik açısından
     * anlamlı ve anahtarlar kişisel veri değil, ürünün kamuya açık kataloğu.
     */
    summary: "Rolün yetkileri değişti.",
    payload: z.strictObject({
      roleId: z.uuid(),
      added: z.array(z.string()),
      removed: z.array(z.string()),
    }),
  },
  "role.archived": {
    name: "role.archived",
    version: 1,
    producer: "authorization",
    actor: "user",
    sensitivity: "operational",
    summary: "Özel rol arşivlendi.",
    payload: z.strictObject({ roleId: z.uuid() }),
  },
  "role.reactivated": {
    name: "role.reactivated",
    version: 1,
    producer: "authorization",
    actor: "user",
    sensitivity: "operational",
    summary: "Özel rol yeniden aktifleştirildi.",
    payload: z.strictObject({ roleId: z.uuid() }),
  },
  /**
   * ### Sözleşme sorumluluğu (CLOS-112 `[074]`)
   *
   * ⚠ Kart *"değişiklik BİLDİRİLİR ve audit edilir"* diyor; bildirim bu üründe olaydan
   * doğuyor, komuttan değil. Servis doğrudan bir bildirim satırı yazsaydı, aynı haberi
   * üretmenin ikinci bir yolu doğar ve yönlendirme matrisi (CLOS-323) atlanabilir olurdu.
   *
   * TEK olay, üç sorumluluk türü için: atama, değiştirme ve kaldırma aynı yaşam
   * döngüsünün hareketleri ve tüketicileri aynı. Altı ayrı olay olsaydı yönlendirme,
   * projector kuralı ve özet metni altı kez yazılır, biri gün gelip ayrışırdı
   * (`invitation.resolved` ile aynı gerekçe).
   */
  "contract.responsibility_changed": {
    name: "contract.responsibility_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "personal",
    summary: "Sözleşme sorumluluğu değişti.",
    /**
     * ⚠ AD YOK, KİMLİK var: olay, sözleşmeyi görme yetkisi olmayan tüketicilere de
     * gidiyor ve sorumlunun adı payload'a girseydi yetki kapısının arkasından dolaşırdı
     * (organizasyon olaylarıyla aynı kural).
     */
    payload: z.strictObject({
      contractId: z.uuid(),
      kind: z.enum(["owner", "contributor", "department"]),
      action: z.enum(["assigned", "removed"]),
      membershipId: z.uuid().nullable(),
      departmentId: z.uuid().nullable(),
    }),
  },
  /**
   * ### Sözleşme tarihleri (CLOS-113 `[075]`)
   *
   * ⚠ Kart, bu kartın SINIRINI da çiziyor: *"CLOS-113 tarihleri kaydeder, sınıflandırır ve
   * KANONİK DEĞİŞİKLİK OLAYINI ÜRETİR. CLOS-232 daha yüksek sırada yenileme, fesih ve
   * kritik tarih hatırlatma kurallarını bu tarihler üzerinde çalıştırır."* Yani hatırlatma
   * kuralları burada değil; burada üretilen olay onların girdisi.
   *
   * Payload TARİH DEĞERİ TAŞIMIYOR, yalnız türünü ve kimliğini: olay, sözleşmeyi görme
   * yetkisi olmayan tüketicilere de gidiyor ve bir fesih bildirim tarihi tek başına
   * ticari bilgi. Tüketici değeri yetkiyle okuma yolundan alıyor.
   */
  "contract.date_changed": {
    name: "contract.date_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "Sözleşme tarihi değişti.",
    payload: z.strictObject({
      contractId: z.uuid(),
      dateId: z.uuid(),
      dateType: z.string().min(1),
      action: z.enum(["set", "removed"]),
    }),
  },
  /**
   * ### Sözleşme yükümlülükleri (CLOS-231 `[196]`)
   *
   * ⚠ Payload ne SORUMLU KİMLİĞİ ne AÇIKLAMA METNİ taşır (`contract.responsibility_changed`
   * ile aynı gerekçe): olay, sözleşmeyi görme yetkisi olmayan tüketicilere de gidiyor.
   * `sourceType` özete giriyor çünkü kart bunu AÇIKÇA ayırt edilebilir istiyor — "AI
   * önerisi kabul edildi" ile "manuel oluşturuldu" farklı cümledir.
   */
  "contract.obligation_changed": {
    name: "contract.obligation_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "Sözleşme yükümlülüğü değişti.",
    payload: z.strictObject({
      contractId: z.uuid(),
      obligationId: z.uuid(),
      action: z.enum(["created", "updated", "suggestion_accepted"]),
      sourceType: z.enum(["manual", "ai_suggested", "ai_accepted"]),
    }),
  },
  /**
   * ⚠ AYRI olay, `contract.obligation_changed`'DEN: kart *"tamamlandı, iptal, gecikme ve
   * yeniden açılma geçmişi korunur"* diyor ve durum geçişi alan güncellemesinden farklı
   * bir kavram — `contract.status_changed` ile `contract.date_changed`'in ayrımıyla aynı.
   */
  "contract.obligation_status_changed": {
    name: "contract.obligation_status_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "Sözleşme yükümlülüğünün durumu değişti.",
    payload: z.strictObject({
      contractId: z.uuid(),
      obligationId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
      /** Bildirim çözümleyicisi bu ÜYELİĞİ kullanıcıya çeviriyor; sorumlu yoksa `null`. */
      responsibleMembershipId: z.uuid().nullable(),
    }),
  },
  /**
   * ### Sözleşme yaşam döngüsü (CLOS-114 `[076]`)
   *
   * ⚠ Kart: *"liste, detay, Arama ve Aktivite projeksiyonları KANONİK DURUM OLAYLARINI
   * tüketir; projeksiyon hatası gerçek durumu geri almaz."* Bu olay o projeksiyonların
   * girdisi — durumu servis yazıyor, projeksiyonlar olaydan besleniyor.
   *
   * `actor: "user"` ama kaynak `schedule`/`workflow` da olabiliyor; o durumda zarfın
   * aktörü nil UUID ve gerçek kaynak payload'daki `source`. Geçiş satırında `actor_id`
   * zaten `NULL` — sistem eylemine bir kullanıcı yazmak, ona yapmadığı bir işi atfetmek
   * olurdu.
   */
  "contract.status_changed": {
    name: "contract.status_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "Sözleşmenin yaşam döngüsü durumu değişti.",
    payload: z.strictObject({
      contractId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
      source: z.enum(["user", "workflow", "schedule"]),
    }),
  },
  /**
   * ### Sözleşme METNİ (CLOS-118 `[086]`)
   *
   * ⚠ Payload METNİ TAŞIMIYOR, yalnız kaç madde olduğunu. Olay, sözleşmeyi görme yetkisi
   * olmayan tüketicilere de gidiyor; sözleşme metni ise ürünün en gizli verisi. Tüketici
   * metni yetkiyle okuma yolundan alıyor (tarih olayıyla aynı karar).
   */
  "contract.content_saved": {
    name: "contract.content_saved",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "Sözleşme metni kaydedildi.",
    payload: z.strictObject({
      contractId: z.uuid(),
      clauses: z.number().int().min(0),
    }),
  },
  /**
   * ### Karşı taraftan gelen revizyon (CLOS-137 `[092]`)
   *
   * ⚠ Payload İÇERİK TAŞIMIYOR, yalnız hangi sözleşmenin kaçıncı versiyonu olduğunu. Kart:
   * *"bildirim ... versiyon içeriğini e-postaya/bildirim payload'ına SIZDIRMAZ."* Tüketici
   * metni yetkiyle okuma yolundan alıyor.
   *
   * ⚠ Kaynak TARAF da payload'da yok: karşı tarafın adı tek başına ticari bilgi ve olay,
   * sözleşmeyi görme yetkisi olmayan tüketicilere de gidiyor.
   */
  /**
   * ### Haricî katılımcı daveti (CLOS-145 `[126]`)
   *
   * ⚠ Payload E-POSTA taşımıyor (`invitation.created` ile aynı karar): davetlinin adresi,
   * daveti görme yetkisi olmayan tüketicilere sızmamalı. Ham token hiçbir olaya girmez.
   */
  "contract.participant_invited": {
    name: "contract.participant_invited",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "personal",
    summary: "Sözleşmeye haricî katılımcı daveti gönderildi.",
    payload: z.strictObject({ invitationId: z.uuid(), contractId: z.uuid() }),
  },
  /** Dört sonuç TEK olayda (`invitation.resolved` ile aynı gerekçe). */
  "contract.participant_invitation_resolved": {
    name: "contract.participant_invitation_resolved",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "personal",
    summary: "Haricî katılımcı daveti sonuçlandı.",
    payload: z.strictObject({
      invitationId: z.uuid(),
      contractId: z.uuid(),
      outcome: z.enum(["accepted", "rejected", "cancelled", "expired"]),
    }),
  },
  /**
   * ### Müvekkil temsil ilişkisi (CLOS-178 `[132]`)
   *
   * TEK ad + `action` alanı (`client.contact_changed` deseni): aktivasyon, askı, sona
   * erme, yeniden aktifleştirme ve kapsam değişikliği aynı yaşam döngüsünün halkaları.
   */
  "client.representation_changed": {
    name: "client.representation_changed",
    version: 1,
    producer: "clients",
    actor: "user",
    sensitivity: "personal",
    summary: "Müvekkil temsil ilişkisi değişti.",
    payload: z.strictObject({
      representationId: z.uuid(),
      clientId: z.uuid(),
      action: z.enum(["activated", "suspended", "ended", "reactivated", "scope_changed"]),
    }),
  },
  /**
   * ### Hizmet ilişkisi yaşam döngüsü (CLOS-246 `[133]`)
   *
   * TEK ad + `action` alanı (`client.representation_changed` deseni): talep, kabul, ret
   * ve iptal aynı aggregate'ın halkaları. Payload YETKİ VE HASSAS İÇERİK TAŞIMAZ (kart
   * kriteri): kimlikler, kapsam anahtarı ve eylem — kapsam açıklaması bile yok.
   * `clientId` null olabilir: müvekkil tarafı başlattığında CRM bağı ancak büronun
   * kabulüyle kurulur.
   *
   * ⚠ `workspaceTenantId` DE null olabilir (K1 · CLOS-1105): talep bir E-POSTAYA
   * gönderildiğinde karşı organizasyon henüz seçilmemiştir ve kabul anında belli olur.
   * Alanı zorunlu tutmak, olayı ya kaybettirir ya da bir organizasyonu TAHMİN ettirirdi;
   * tahmin, adresin Closer'daki karşılığını sızdıran bir yan kanal olurdu. Tüketici
   * (aktivite projeksiyonu) satırı ilişki kimliğine bağlıyor, bu alanı okumuyor.
   */
  "client.service_relationship_changed": {
    name: "client.service_relationship_changed",
    version: 1,
    producer: "clients",
    actor: "user",
    sensitivity: "operational",
    summary: "Hizmet ilişkisi değişti.",
    payload: z.strictObject({
      relationshipId: z.uuid(),
      workspaceTenantId: z.uuid().nullable(),
      clientId: z.uuid().nullable(),
      scope: z.string(),
      /** `suspended`/`resumed`/`ended` yaşam döngüsü halkaları CLOS-248 `[134]` ile geldi. */
      action: z.enum([
        "requested",
        "accepted",
        "rejected",
        "cancelled",
        "suspended",
        "resumed",
        "ended",
      ]),
    }),
  },
  /**
   * ### Sözleşme yorumu (CLOS-150 `[137]`)
   *
   * TEK ad + `action` alanı. Payload YORUM METNİ TAŞIMAZ: olay tüketicilere güvenli
   * kaynak BAĞLANTISI verir (yorum kimliği + versiyon), içerik okuma yolunda yetkiyle
   * çözülür — olay okuma yetkisi yaratmaz (kart kriteri).
   */
  "contract.comment_changed": {
    name: "contract.comment_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "Sözleşme yorumu değişti.",
    payload: z.strictObject({
      commentId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      /** `resolved`/`reopened` tartışma durumudur, FORMAL KARAR DEĞİL (CLOS-152 `[139]`);
       * `shared`/`unshared` görünürlük halkalarıdır (CLOS-153 `[141]`). */
      action: z.enum([
        "created",
        "edited",
        "deleted",
        "resolved",
        "reopened",
        "shared",
        "unshared",
      ]),
    }),
  },
  /**
   * ### Çok taraflı müzakere (CLOS-252 `[145]`)
   *
   * TEK ad + `action`. Payload öneri METNİ taşımaz; `decidingTenantId` bekleyen-işlem
   * tüketicisine "sıra kimde" sorusunu cevaplar — tüketici erişimi okuma anında yeniden
   * doğrular. Müzakere kararı FORMAL onay/mutabakat/imza DEĞİLDİR.
   */
  "contract.negotiation_changed": {
    name: "contract.negotiation_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    payload: z.strictObject({
      negotiationId: z.uuid(),
      contractId: z.uuid(),
      /**
       * `cancelled` CLOS-233 `[170]` DEV-174 (CLOS-922/CLOS-925) ile eklendi — `withdrawn`
       * yalnız önerenin kararı, `cancelled` sözleşme sahibinin workflow yönetimi kararı.
       */
      action: z.enum([
        "opened",
        "countered",
        "withdrawn",
        "item_decided",
        "resolved",
        "applied",
        "cancelled",
      ]),
      decidingTenantId: z.uuid().nullable(),
    }),
    summary: "Sözleşme müzakeresi değişti.",
  },
  /**
   * ### Sözleşme inceleme akışı (CLOS-155 `[148]`)
   *
   * TEK ad + `action` (müzakere ve hizmet ilişkisi deseni): başlatma, hatırlatma ve
   * sonuçlandırma aynı akışın halkaları ve kendi kartlarıyla (`[149]`, `[150]`) bu enum'a
   * eklenecekler. Her halkaya yeni bir olay adı açmak, tüketicileri her kartta yeniden
   * elden geçirmek olurdu.
   *
   * ⚠ Payload İÇERİK TAŞIMAZ: açıklama metni, son tarih DEĞERİ ve inceleyici kimlikleri
   * yok — yalnız kimlikler, eylem ve KAÇ inceleyici olduğu. Son tarih tek başına ticari
   * bilgidir (`contract.date_changed` ile aynı gerekçe) ve inceleyici listesi, akışı
   * göremeyen bir tüketiciye kimin çağrıldığını söylerdi.
   */
  "contract.review_changed": {
    name: "contract.review_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    payload: z.strictObject({
      reviewId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      /**
       * `[150]` CLOS-157 sonuç halkalarını ekledi; `task_cancelled`/`task_reassigned`
       * CLOS-233 `[170]` DEV-174 (CLOS-922/CLOS-923) ile — yeni ad değil, aynı ailenin
       * eylemleri.
       */
      action: z.enum([
        "started",
        "cancelled",
        "task_completed",
        "task_changes_requested",
        "task_cancelled",
        "task_reassigned",
        "completed",
        "changes_requested",
      ]),
      reviewerCount: z.number().int().min(0),
    }),
    summary: "Sözleşme inceleme akışı değişti.",
  },
  /**
   * ### İmza talebi (CLOS-164 `[157]`)
   *
   * ⚠ Payload UYGUNLUK POLİTİKASINI taşıyor ama KANIT KİMLİĞİNİ taşımıyor: hangi onay ya
   * da mutabakat akışına dayanıldığı, akışı göremeyen bir tüketiciye organizasyonun iç
   * karar zincirini açardı. Politika adı ise ürünün kamuya açık kavramı.
   */
  "contract.signature_request_changed": {
    name: "contract.signature_request_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    payload: z.strictObject({
      requestId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      eligibilityPolicy: z.enum(["NONE", "VALID_APPROVAL", "VALID_CONSENSUS"]),
      /**
       * `signer_reassigned` CLOS-233 `[170]` DEV-174 (CLOS-922/CLOS-926) ile eklendi:
       * `signers_set` gibi yalnız GÖNDERİLMEMİŞ plan üzerinde — projeksiyon bu eylemi
       * `signers_set` ile aynı şekilde yok sayar (henüz görev doğmamış).
       */
      action: z.enum(["created", "signers_set", "sent", "cancelled", "signer_reassigned"]),
    }),
    summary: "Sözleşme imza talebi değişti.",
  },
  /**
   * ### İmzacı durum değişimi — KULLANICI komutu (CLOS-166 `[159]`)
   *
   * ⚠ Payload imzacı KİMLİĞİNİ taşıyor ama kişinin adını/e-postasını taşımıyor: kim
   * imzaladığı sözleşmeyi göremeyen bir tüketiciye açılmamalı. Görev projeksiyonu
   * kimlikleri kaynaktan okuyor (`[149]`'un deseni).
   */
  "contract.signer_status_changed": {
    name: "contract.signer_status_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    payload: z.strictObject({
      requestId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      signerId: z.uuid(),
      fromStatus: z.string(),
      /**
       * `cancelled` CLOS-233 `[170]` DEV-174 (CLOS-922/CLOS-926) ile eklendi: tek bir
       * imzacının görevinin YÖNETİMSEL iptali — imzacının kendi `decline` komutundan AYRI.
       */
      action: z.enum(["viewed", "signed", "declined", "cancelled"]),
    }),
    summary: "Sözleşme imzacısının durumu değişti.",
  },
  /**
   * ### İmzacı durum değişimi — SAĞLAYICI kaynaklı (CLOS-166 `[159]`)
   *
   * ⚠ Kullanıcı komutundan AYRI ad: aktörü sistem, çünkü geçişi bir kişi değil adapter
   * getiriyor. Aynı ada sıkıştırsaydık "bu imzayı kim attı?" sorusu olayın içinde
   * belirsizleşirdi. Sağlayıcı durumu kanonik durumun kaynağı değil — geçiş yine matristen
   * geçiyor.
   */
  "contract.signer_status_synced": {
    name: "contract.signer_status_synced",
    version: 1,
    producer: "contracts",
    actor: "system",
    sensitivity: "confidential",
    payload: z.strictObject({
      requestId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      signerId: z.uuid(),
      fromStatus: z.string(),
      action: z.enum(["viewed", "signed", "declined", "failed"]),
    }),
    summary: "Sözleşme imzacısının durumu sağlayıcıdan güncellendi.",
  },
  /**
   * ### İmza talebinin süresi dolması (CLOS-166 `[159]`)
   *
   * `superseded` kardeşiyle aynı: SİSTEM aktörü, çünkü süre sonuna kimse karar vermiyor.
   */
  "contract.signature_request_expired": {
    name: "contract.signature_request_expired",
    version: 1,
    producer: "contracts",
    actor: "system",
    sensitivity: "operational",
    payload: z.strictObject({
      requestId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      expiredSignerCount: z.number().int().nonnegative(),
    }),
    summary: "İmza talebinin süresi doldu.",
  },
  /**
   * ### İmza talebinin sonuçlanması (CLOS-166 `[159]`)
   *
   * ⚠ İmzacı geçişinden AYRI olay: "bir kişi imzaladı" ile "talep tamamlandı" farklı
   * gerçekler. Tüketiciler (Bekleyen İşlemler, bildirim) sonuca ayrı tepki veriyor.
   */
  "contract.signature_request_settled": {
    name: "contract.signature_request_settled",
    version: 1,
    producer: "contracts",
    actor: "system",
    sensitivity: "confidential",
    payload: z.strictObject({
      requestId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      action: z.enum(["completed", "declined", "failed"]),
    }),
    summary: "Sözleşme imza talebi sonuçlandı.",
  },
  /**
   * ### Bağlı sözleşme oluşturuldu (CLOS-253 `[161]`)
   *
   * ⚠ Payload İKİ kaydın kimliğini taşıyor ama BAŞLIKLARINI taşımıyor: karşı kayda erişimi
   * olmayan bir tüketiciye adı sızmamalı. İlişkinin varlığı kamuya açık, kimliği değil.
   */
  "contract.linked_contract_created": {
    name: "contract.linked_contract_created",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    payload: z.strictObject({
      linkId: z.uuid(),
      sourceContractId: z.uuid(),
      sourceVersionId: z.uuid(),
      targetContractId: z.uuid(),
      linkType: z.enum(["addendum", "amendment", "renewal"]),
    }),
    summary: "Sözleşmeden bağlı kayıt oluşturuldu.",
  },
  /**
   * ### Manuel (ıslak) imza kaydı (CLOS-167 `[160]`)
   *
   * ⚠ Sağlayıcı geçişinden AYRI ad ve aktörü GERÇEK KULLANICI: ıslak imzayı sisteme
   * geçiren kişi bir tasarrufta bulunuyor. Payload YÖNTEMİ taşıyor ama kanıt belgesini
   * taşımıyor — belgenin kendisi olaya değil arşive ait.
   */
  "contract.manual_signature_recorded": {
    name: "contract.manual_signature_recorded",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    payload: z.strictObject({
      requestId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      signerId: z.uuid(),
      method: z.enum(["wet_ink", "qualified_certificate", "in_person"]),
    }),
    summary: "Sözleşme için manuel imza kaydedildi.",
  },
  /**
   * ### İmzalı belgenin arşive bağlanması (CLOS-167 `[160]`)
   *
   * ⚠ Payload belge İÇERİĞİNİ taşımıyor (kartın açık kuralı); yalnız depo nesnesinin
   * kimliğini. İçeriği olaya koysaydık, sözleşmeyi görme yetkisi olmayan bir tüketici
   * imzalı metni okurdu.
   */
  "contract.signed_document_attached": {
    name: "contract.signed_document_attached",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    payload: z.strictObject({
      requestId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      storageObjectId: z.uuid(),
    }),
    summary: "Sözleşmenin imzalı belgesi arşivlendi.",
  },
  /**
   * ### İmza talebinin versiyon değişimiyle geçersizleşmesi (CLOS-164 `[157]`)
   *
   * Onay ve mutabakatın kardeşi: SİSTEM aktörü, çünkü geçersizleşmeye kimse karar vermiyor.
   */
  "contract.signature_request_superseded": {
    name: "contract.signature_request_superseded",
    version: 1,
    producer: "contracts",
    actor: "system",
    sensitivity: "operational",
    payload: z.strictObject({
      requestId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      newVersionId: z.uuid(),
    }),
    summary: "İmza talebi yeni versiyon nedeniyle geçersizleşti.",
  },
  /**
   * ### Mutabakatın versiyon değişimiyle geçersizleşmesi (CLOS-163 `[156]`)
   *
   * Onayın `[153]`'teki kardeşi: SİSTEM aktörü, çünkü geçersizleşmeye kimse karar vermiyor.
   */
  "contract.reconciliation_superseded": {
    name: "contract.reconciliation_superseded",
    version: 1,
    producer: "contracts",
    actor: "system",
    sensitivity: "operational",
    payload: z.strictObject({
      reconciliationId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      newVersionId: z.uuid(),
      affectedTaskCount: z.number().int().min(0),
    }),
    summary: "Mutabakat akışı yeni versiyon nedeniyle geçersizleşti.",
  },
  /**
   * ### Taraflar arası mutabakat (CLOS-161 `[154]`)
   *
   * TEK ad + `action`; karar halkaları `[155]` CLOS-162 ile eklenecek.
   *
   * ⚠ Payload TARAF ADLARINI ve temsilci kimliklerini taşımıyor: kimin hangi taraf adına
   * karar verdiği, akışı göremeyen bir tüketiciye iki organizasyonun ticari ilişkisini
   * açardı. Yalnız kimlikler, eylem ve kaç taraf olduğu.
   */
  "contract.reconciliation_changed": {
    name: "contract.reconciliation_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    payload: z.strictObject({
      reconciliationId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      /** `[155]` CLOS-162 karar halkalarını ekledi — yeni ad değil, aynı ailenin eylemleri. */
      action: z.enum([
        "started",
        "cancelled",
        "party_agreed",
        "party_disagreed",
        "party_changes_requested",
        "agreed",
        "disagreed",
        "changes_requested",
      ]),
      partyCount: z.number().int().min(0),
    }),
    summary: "Sözleşme mutabakat akışı değişti.",
  },
  /**
   * ### Onayın versiyon değişimiyle geçersizleşmesi (CLOS-160 `[153]`)
   *
   * ⚠ AYRI ad ve SİSTEM aktörü: geçersizleşmeye kimse karar vermiyor, sözleşmenin yeni bir
   * versiyonu doğduğu için oluyor. `contract.approval_changed` katalogda `actor: "user"` ve
   * `buildEvent` aktörü zorluyor — `[149]` hatırlatma olayında öğrenilen ders.
   *
   * ⚠ Payload kararları ve onaylayıcıları taşımıyor: yalnız hangi akışın, hangi versiyon
   * yüzünden geçersizleştiği ve kaç görev etkilendiği.
   */
  "contract.approval_superseded": {
    name: "contract.approval_superseded",
    version: 1,
    producer: "contracts",
    actor: "system",
    sensitivity: "operational",
    payload: z.strictObject({
      approvalId: z.uuid(),
      contractId: z.uuid(),
      /** Akışın çakılı olduğu ESKİ versiyon. */
      versionId: z.uuid(),
      /** Geçersizleşmeye yol açan YENİ versiyon. */
      newVersionId: z.uuid(),
      affectedTaskCount: z.number().int().min(0),
    }),
    summary: "Onay akışı yeni versiyon nedeniyle geçersizleşti.",
  },
  /**
   * ### İç onay akışı (CLOS-158 `[151]`)
   *
   * TEK ad + `action` (inceleme ve müzakere deseni). Karar halkaları `[152]` CLOS-159,
   * versiyon değişiminde geçersizleşme `[153]` CLOS-160 ile bu enum'a eklenecek.
   *
   * ⚠ Payload onaylayıcı kimliklerini, gerekçeyi ve son tarih DEĞERİNİ taşımıyor; yalnız
   * kimlikler, akış türü, eylem ve KAÇ aşama olduğu. Kimin onaylayacağı, akışı göremeyen
   * bir tüketiciye organizasyonun karar hiyerarşisini açardı.
   */
  "contract.approval_changed": {
    name: "contract.approval_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    payload: z.strictObject({
      approvalId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      mode: z.enum(["serial", "parallel"]),
      /**
       * `[152]` CLOS-159 karar halkalarını ekledi; `task_cancelled`/`task_reassigned`
       * CLOS-233 `[170]` DEV-174 (CLOS-922/CLOS-924) ile — yeni ad değil, aynı ailenin
       * eylemleri.
       */
      action: z.enum([
        "started",
        "cancelled",
        "task_approved",
        "task_rejected",
        "task_changes_requested",
        "task_cancelled",
        "task_reassigned",
        "stage_approved",
        "approved",
        "rejected",
        "changes_requested",
      ]),
      stageCount: z.number().int().min(0),
    }),
    summary: "Sözleşme onay akışı değişti.",
  },
  /**
   * ### İnceleme hatırlatması (CLOS-156 `[149]`)
   *
   * ⚠ AYRI ad ve bu bir tercih değil zorunluluk: hatırlatmayı zamanlanmış iş üretiyor ve
   * gerçek bir kullanıcısı yok. `contract.review_changed` katalogda `actor: "user"` ve
   * `buildEvent` aktörü zorluyor — aynı ada `reminded` eylemi eklemek, sistemin kullanıcı
   * kılığına girmesi olurdu.
   *
   * ⚠ Payload hassas içerik taşımıyor (kartın kriteri): sözleşme başlığı, açıklama ve son
   * tarih DEĞERİ yok; yalnız kimlikler ve hangi eşiğin geçildiği. Teslim tüketicisi
   * erişimi okuma anında yeniden doğruluyor.
   */
  "contract.review_reminder_due": {
    name: "contract.review_reminder_due",
    version: 1,
    producer: "contracts",
    // Hatırlatmanın gerçek kullanıcısı YOKTUR ve uydurulmaz: eşiği zaman geçirdi.
    actor: "system",
    sensitivity: "operational",
    payload: z.strictObject({
      reviewId: z.uuid(),
      taskId: z.uuid(),
      contractId: z.uuid(),
      thresholdKey: z.string(),
    }),
    summary: "İnceleme görevinin hatırlatma eşiği geçildi.",
  },
  /**
   * ### Kritik tarih hatırlatması (CLOS-232 `[182]`)
   *
   * ⚠ Payload TARİH DEĞERİNİ TAŞIMIYOR — yalnız kimlikler ve eşik anahtarı. Değeri
   * taşısaydık olay yayıldıktan sonra tarih değiştiğinde yük bayatlar ve tüketici
   * gerçekte olmayan bir günü gösterirdi; kaynak gerçek `dateId`'nin kendisi.
   *
   * ⚠ `dateType` taşınıyor çünkü bildirim başlığı türü bilmek zorunda ("Yenileme tarihi
   * yaklaşıyor" ile "Fesih bildirim tarihi yaklaşıyor" aynı cümle değil) ve tür değişmez
   * bir sınıflandırma — değer gibi bayatlamıyor.
   */
  "contract.date_reminder_due": {
    name: "contract.date_reminder_due",
    /**
     * ⚠ SÜRÜM 2: yük alıcı listesini taşımaya başladı (CLOS-232 uyum kalemi).
     *
     * Defter kişi bazına geçince "bu sinyal kimin için üretildi" sorusunun cevabı
     * üretim anında biliniyor ve taşınması gerekiyor — teslim anında yeniden
     * hesaplansaydı zaten hatırlatılmış kişiler de listeye girerdi.
     */
    version: 2,
    producer: "contracts",
    // Eşiği zaman geçirdi; düğmeye basan yok. Aktör uydurulmaz.
    actor: "system",
    sensitivity: "operational",
    payload: z.strictObject({
      dateId: z.uuid(),
      contractId: z.uuid(),
      dateType: z.string(),
      thresholdKey: z.string(),
      /**
       * ⚠ HEDEF KÜMESİ, İZİN DEĞİL: bu liste "kime üretildi" diyor, "kim görebilir"
       * demiyor. Teslim anında kayıt erişimi yeniden soruluyor ve liste yalnız
       * DARALIYOR — yükteki bir kimlik, erişimi olmayan birine kapı açmıyor.
       */
      recipientUserIds: z.array(z.uuid()).min(1),
    }),
    summary: "Sözleşmenin kritik tarih hatırlatma eşiği geçildi.",
  },
  /**
   * ### Yükümlülük hatırlatması (CLOS-231 `[196]`, alt görev CLOS-1033)
   *
   * `contract.date_reminder_due` ile BİREBİR aynı desen ve aynı gerekçe: değer taşınmıyor
   * (bayatlama), hedef kümesi üretim anında biliniyor ve DARALTMA amaçlı taşınıyor.
   */
  "contract.obligation_reminder_due": {
    name: "contract.obligation_reminder_due",
    version: 1,
    producer: "contracts",
    actor: "system",
    sensitivity: "operational",
    payload: z.strictObject({
      obligationId: z.uuid(),
      contractId: z.uuid(),
      thresholdKey: z.string(),
      recipientUserIds: z.array(z.uuid()).min(1),
    }),
    summary: "Sözleşme yükümlülüğünün hatırlatma eşiği geçildi.",
  },
  /**
   * ### Nesne paylaşımı (CLOS-250 `[143]`)
   *
   * TEK ad + `action`. Payload hassas içerik ve nesne İÇERİĞİ taşımaz — tüketiciler
   * okuma anında tenant, ilişki ve paylaşım satırını yeniden doğrular.
   */
  "contract.object_share_changed": {
    name: "contract.object_share_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    payload: z.strictObject({
      shareId: z.uuid(),
      contractId: z.uuid(),
      /**
       * ⚠ Tür listesi KOPYALANMIYOR, tek kaynaktan geliyor. Elle yazılsaydı yeni bir nesne
       * türü eklendiğinde katalog geride kalır ve olay yayınlama anında 500 üretirdi —
       * ölçüldü (CLOS-250 revizyon/müzakere turu). Liste üç yerde yaşıyor: şema, DB kısıtı
       * ve burası; üçünün de aynı kaynağa bakması şart.
       */
      objectType: z.enum(SHAREABLE_OBJECT_TYPES),
      /** `updated`=kapsam revizyonu, `revoked`=geri çekme (CLOS-251 `[144]`). */
      action: z.enum(["created", "updated", "revoked"]),
    }),
    summary: "Sözleşme nesnesi paylaşıldı.",
  },
  /**
   * ### Çalışma nesnesi görünürlüğü (CLOS-154 `[140]`)
   *
   * TEK ad + `action`. Payload ÖZEL İÇERİK taşımaz — tüketiciler okuma anında tenant,
   * permission ve nesne görünürlüğünü yeniden kontrol eder.
   */
  "contract.work_item_changed": {
    name: "contract.work_item_changed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    payload: z.strictObject({
      workItemId: z.uuid(),
      contractId: z.uuid(),
      action: z.enum(["created", "shared", "unshared"]),
    }),
    summary: "Sözleşme çalışma nesnesi değişti.",
  },
  /**
   * ### Yorum mention'ı (CLOS-151 `[138]`)
   *
   * Bildirim SİNYALİ: hedef kullanıcı için güvenli kimlikler taşır, yorum/sözleşme metni
   * TAŞIMAZ. Teslim tüketicisi okuma anında erişimi yeniden doğrular — olay okuma yetkisi
   * yaratmaz.
   */
  "contract.comment_mentioned": {
    name: "contract.comment_mentioned",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "Yorumda bir kullanıcı anıldı.",
    payload: z.strictObject({
      commentId: z.uuid(),
      contractId: z.uuid(),
      versionId: z.uuid(),
      mentionedUserId: z.uuid(),
    }),
  },
  /**
   * ### Portföy sorumluluğu (CLOS-247 `[135]`)
   *
   * Yalnız BÜRO İÇİ projection'lar için; payload private not veya müvekkil içeriği
   * TAŞIMAZ — yalnız kimlikler ve eylem.
   */
  "client.portfolio_assignment_changed": {
    name: "client.portfolio_assignment_changed",
    version: 1,
    producer: "clients",
    actor: "user",
    sensitivity: "operational",
    summary: "Müvekkil portföy sorumluluğu değişti.",
    payload: z.strictObject({
      assignmentId: z.uuid(),
      clientId: z.uuid(),
      relationshipId: z.uuid(),
      action: z.enum(["assigned", "reassigned", "closed"]),
    }),
  },
  /**
   * ### Haricî organizasyon daveti (CLOS-245 `[131]`)
   *
   * ⚠ Payload yalnız kimlikler — hedef organizasyonun ADI bile yok: iki tarafın ticari
   * ilişkisi, olayı tüketen herkese açılmamalı.
   */
  "contract.org_invited": {
    name: "contract.org_invited",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "personal",
    summary: "Sözleşmeye haricî organizasyon daveti gönderildi.",
    payload: z.strictObject({ invitationId: z.uuid(), contractId: z.uuid() }),
  },
  "contract.org_invitation_resolved": {
    name: "contract.org_invitation_resolved",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "personal",
    summary: "Haricî organizasyon daveti sonuçlandı.",
    payload: z.strictObject({
      invitationId: z.uuid(),
      contractId: z.uuid(),
      outcome: z.enum(["accepted", "rejected", "cancelled", "expired"]),
    }),
  },
  /**
   * SÖZLEŞMEYE HİZMET SAĞLAYICI ATANDI (CLOS-1185).
   *
   * ⚠ Yük SAĞLAYICI TENANT'INI taşıyor: haber verilecek taraf sözleşmenin sahibi değil,
   * görevlendirilen büro. Taşımasaydı çözümleyici hedef kitleyi hiç bulamazdı.
   *
   * ⚠ Ad ya da kapsam TAŞIMIYOR: olay, kaydı görme yetkisi olmayan yerlerde de okunabilir
   * ve içerik sızdırmamalı — kayıt erişimi olayının aynı kuralı.
   */
  "contract.service_assignment_activated": {
    name: "contract.service_assignment_activated",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "Sözleşmeye hukuki hizmet sağlayıcı atandı.",
    payload: z.strictObject({
      contractId: z.uuid(),
      assignmentId: z.uuid(),
      servedPartyId: z.uuid(),
      providerTenantId: z.uuid(),
    }),
  },
  "contract.counterparty_revision_uploaded": {
    name: "contract.counterparty_revision_uploaded",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "Karşı taraftan gelen revizyon yüklendi.",
    payload: z.strictObject({
      contractId: z.uuid(),
      versionId: z.uuid(),
      versionNumber: z.number().int().min(1),
    }),
  },
  /**
   * ### Kayıt erişimi (CLOS-91 `[077]`)
   *
   * ⚠ Kart: *"verilen erişim BİLDİRİLİR ve audit edilir."* Erişim kazandığını bilmeyen
   * kişi o erişimi kullanamaz — ve daha kötüsü, kendisine açılan bir sözleşmeden habersiz
   * kalır.
   *
   * Payload kayıt ADINI taşımıyor, yalnız türünü ve kimliğini: olay, kaydı görme yetkisi
   * olmayan tüketicilere de gidiyor ve bir sözleşme başlığı tek başına ticari bilgi.
   */
  "record.access_granted": {
    name: "record.access_granted",
    /** ⚠ SÜRÜM 2: özne kümesine `client_area` eklendi (ADR-0038). */
    version: 2,
    producer: "recordAccess",
    actor: "user",
    sensitivity: "confidential",
    summary: "Bir kayda erişim verildi.",
    payload: z.strictObject({
      recordType: z.string().min(1),
      recordId: z.uuid(),
      /**
       * ⚠ `client_area` (ADR-0038): grant kişiye değil MÜVEKKİL ALANINA verilebiliyor.
       * Kümeyi genişletmeseydik alan grant'i olay kataloğunda temsil edilemez ve
       * denetim "bu erişim kime verildi" sorusunu cevaplayamazdı.
       */
      subjectType: z.enum(["user", "membership", "client_area"]),
      subjectId: z.uuid(),
      level: z.enum(["read", "write"]),
    }),
  },
  /**
   * ### Kayıt erişimi kapanışı (CLOS-92 `[078]`)
   *
   * ⚠ Kart: *"bildirim ve audit oluşur."* Erişimini kaybettiğini bilmeyen kullanıcı, dün
   * açtığı bir bağlantının bugün neden çalışmadığını anlamaz ve bunu bir arıza sanır.
   *
   * Özne payload'da YOK: bildirim hedefi zarfın `affected_user`'ı üzerinden çözülüyor ve
   * kimin erişimini kaybettiği, kaydı görmeyen tüketicilere gitmemeli.
   */
  "record.access_closed": {
    name: "record.access_closed",
    version: 1,
    producer: "recordAccess",
    actor: "user",
    sensitivity: "confidential",
    summary: "Bir kayda erişim sona erdi.",
    payload: z.strictObject({
      recordType: z.string().min(1),
      recordId: z.uuid(),
      reason: z.enum(["revoked", "expired", "membership_ended"]),
    }),
  },
  "file.uploaded": {
    name: "file.uploaded",
    version: 1,
    producer: "files",
    actor: "user",
    sensitivity: "operational",
    // Dosya ADI bilerek YOK: "2026-ihale-teklifi-gizli.pdf" tek başına içerik sızdırır
    // ve olay payload'ı, dosyayı görme yetkisi olmayan tüketicilere de gider.
    summary: "Yükleme doğrulandı; dosya karantinada tarama bekliyor.",
    payload: z.strictObject({
      objectId: z.uuid(),
      domain: z.string().min(1),
      entityId: z.uuid(),
      contentType: z.string().min(1),
      sizeBytes: z.number().int().nonnegative(),
    }),
  },
  "file.scan_clean": {
    name: "file.scan_clean",
    version: 1,
    producer: "files",
    // Tarayıcı sonucunun gerçek kullanıcısı YOKTUR ve uydurulmaz.
    actor: "system",
    sensitivity: "operational",
    summary: "Tarama temiz; dosya teslim edilebilir duruma geçti.",
    payload: z.strictObject({ objectId: z.uuid() }),
  },
  "file.scan_infected": {
    name: "file.scan_infected",
    version: 1,
    producer: "files",
    actor: "system",
    sensitivity: "operational",
    summary: "Tarama zararlı buldu; dosya kalıcı olarak reddedildi.",
    payload: z.strictObject({ objectId: z.uuid(), signature: z.string().min(1) }),
  },
  "file.scan_failed": {
    name: "file.scan_failed",
    version: 1,
    producer: "files",
    actor: "system",
    sensitivity: "operational",
    summary: "Tarayıcıya ulaşılamadı; dosya teslim edilemez, yeniden denenecek.",
    payload: z.strictObject({ objectId: z.uuid(), reason: z.string().min(1) }),
  },
  /**
   * ### Müvekkil güncellendi (CLOS-170 `[117]`)
   *
   * ⚠ Payload DEĞER TAŞIMIYOR, yalnız hangi alanların değiştiğini. Kart: *"içerik veya
   * hassas alanlar gereksiz payload'a yazılmaz."* Kimlik ve vergi numarası bir yana, adın
   * kendisi bile olaya girmiyor: olay, müvekkili görme yetkisi olmayan tüketicilere de
   * ulaşabiliyor ve güncel hâli okuma yolundan YETKİYLE almak zorundalar.
   *
   * ⚠ `sensitivity: personal` — değişen alan adları kişisel veri sınıfını işaret ediyor
   * ("nationalId değişti" bilgisi bile bir kişi hakkında bir şey söyler).
   */
  "client.updated": {
    name: "client.updated",
    version: 1,
    producer: "clients",
    actor: "user",
    sensitivity: "personal",
    summary: "Müvekkil bilgileri güncellendi.",
    payload: z.strictObject({
      clientId: z.uuid(),
      changedFields: z.array(z.string().min(1)).min(1),
    }),
  },
  /**
   * ### Müvekkil birleştirildi (CLOS-171 `[118]`)
   *
   * ⚠ Payload AD taşımıyor, yalnız iki kimlik ve taşınan ilişki SINIFLARININ adları. Kart:
   * *"güvenli domain olayı Arama, aktivite ve AI projection'larını YENİDEN KURAR"* — yeniden
   * kurmak için hangi kaydın hangisine gittiğini bilmek yeterli; içerik okuma yolundan
   * yetkiyle alınıyor.
   */
  "client.merged": {
    name: "client.merged",
    version: 1,
    producer: "clients",
    actor: "user",
    sensitivity: "personal",
    summary: "Mükerrer müvekkil başka bir kayda birleştirildi.",
    payload: z.strictObject({
      sourceId: z.uuid(),
      targetId: z.uuid(),
      referenceClasses: z.array(z.string().min(1)),
    }),
  },
  /**
   * ### Müvekkil durumu değişti (CLOS-172 `[119]`)
   *
   * ⚠ Payload AD taşımıyor: durum değişimi müvekkili görme yetkisi olmayan tüketicilere de
   * ulaşıyor. `from`/`to` ise projection'ların yeniden kurulması için şart — hangi kaydın
   * ne zaman gündemden çıktığı, aramanın ve aktivitenin cevaplaması gereken soru.
   */
  "client.status_changed": {
    name: "client.status_changed",
    version: 1,
    producer: "clients",
    actor: "user",
    sensitivity: "operational",
    summary: "Müvekkil arşivlendi ya da yeniden aktifleştirildi.",
    payload: z.strictObject({
      clientId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
    }),
  },
  /**
   * ### Müvekkil portföyüne sözleşme bağlandı / bağ kaldırıldı (CLOS-173 `[120]`)
   *
   * ⚠ Payload SÖZLEŞME BAŞLIĞI taşımıyor, yalnız iki kimlik. Kart: *"güvenli domain olayı
   * ... ERİŞİM YETKİSİ TAŞIMAZ."* Başlığı koysaydık, sözleşmeyi görme yetkisi olmayan
   * tüketiciler onu olay akışından okurdu.
   *
   * ⚠ Bağlama ve kaldırma AYRI adlar: ikisi farklı iş olayları ve tüketiciler (aktivite,
   * AI) birine tepki verip ötekine vermeyebilir. Tek ada `action` alanı koysaydık, her
   * tüketici payload'ın içine bakarak dallanmak zorunda kalırdı.
   */
  "client.contract_linked": {
    name: "client.contract_linked",
    version: 1,
    producer: "clients",
    actor: "user",
    sensitivity: "operational",
    summary: "Sözleşme müvekkil portföyüne bağlandı.",
    payload: z.strictObject({ clientId: z.uuid(), contractId: z.uuid() }),
  },
  "client.contract_unlinked": {
    name: "client.contract_unlinked",
    version: 1,
    producer: "clients",
    actor: "user",
    sensitivity: "operational",
    summary: "Sözleşmenin müvekkil portföyüyle bağı kaldırıldı.",
    payload: z.strictObject({ clientId: z.uuid(), contractId: z.uuid() }),
  },
  /**
   * ### Müvekkil iletişim kişisi değişti (CLOS-174 `[121]`)
   *
   * ⚠ TEK ad, `action` alanıyla — bağlama/kaldırma çiftinden (`client.contract_*`) farklı
   * olarak burada üç eylem (oluşturma, güncelleme, pasifleştirme) tüketiciler için AYNI
   * anlama geliyor: müvekkilin iletişim listesi değişti, projeksiyonu tazele. Üç ayrı ad,
   * her tüketiciye üçünü de dinleme yükü bindirirdi.
   *
   * ⚠ Payload AD, E-POSTA ve TELEFON taşımıyor. Kart: *"hassas iletişim alanları gereksiz
   * liste, OLAY veya log payload'ına taşınmaz."*
   */
  "client.contact_changed": {
    name: "client.contact_changed",
    version: 1,
    producer: "clients",
    actor: "user",
    sensitivity: "personal",
    summary: "Müvekkilin iletişim kişisi eklendi, güncellendi ya da pasifleştirildi.",
    payload: z.strictObject({
      clientId: z.uuid(),
      contactId: z.uuid(),
      action: z.enum(["created", "updated", "deactivated"]),
    }),
  },
  /**
   * ### Sponsorlu Müvekkil Alanı kuruldu (CLOS-175 `[122]`)
   *
   * ⚠ Payload MÜVEKKİL VERİSİ taşımıyor — ad bile yok, yalnız iki kimlik. Kart: *"güvenli
   * domain olayı profil, kullanım ve aktivite projection'larını besler; HASSAS MÜVEKKİL
   * VERİSİ TAŞIMAZ."*
   */
  "client.workspace_created": {
    name: "client.workspace_created",
    version: 1,
    producer: "clients",
    actor: "user",
    sensitivity: "operational",
    summary: "Müvekkil için Sponsorlu Müvekkil Alanı kuruldu.",
    payload: z.strictObject({ clientId: z.uuid(), workspaceTenantId: z.uuid() }),
  },
  /**
   * ### Müvekkil Temsilcisi davet edildi (CLOS-177 `[124]`)
   *
   * ⚠ Payload E-POSTA taşımıyor (`invitation.created` ile aynı karar): davetlinin adresi,
   * daveti görme yetkisi olmayan tüketicilere sızmamalı. Ham token zaten hiçbir olaya
   * girmez — yalnız davet e-postasında yaşar.
   */
  "client.representative_invited": {
    name: "client.representative_invited",
    version: 1,
    producer: "clients",
    actor: "user",
    sensitivity: "personal",
    summary: "Müvekkil için temsilci daveti gönderildi.",
    payload: z.strictObject({ invitationId: z.uuid(), clientId: z.uuid() }),
  },
  /**
   * Dört sonuç TEK olayda (`invitation.resolved` ile aynı gerekçe): kabul, ret, iptal ve
   * süre sonu aynı yaşam döngüsünün kapanışı; dört ayrı olay yönlendirme matrisini dört
   * kez yazdırırdı.
   */
  "client.representative_invitation_resolved": {
    name: "client.representative_invitation_resolved",
    version: 1,
    producer: "clients",
    actor: "user",
    sensitivity: "personal",
    summary: "Müvekkil temsilcisi daveti sonuçlandı.",
    payload: z.strictObject({
      invitationId: z.uuid(),
      clientId: z.uuid(),
      outcome: z.enum(["accepted", "rejected", "cancelled", "expired"]),
    }),
  },
  /**
   * ## TİCARİ OLAY AİLESİ (CLOS-884 `[KARAR 06]`)
   *
   * Belge: *"Olaylar provider callback'lerinin KOPYASI DEĞİL kanonik Closer domain
   * olayları olmalıdır"* ve *"Sağlayıcı belli olmadan provider webhook adlarını kanonik
   * domain event olarak kullanma."* Bu yüzden hiçbir adda sağlayıcı geçmiyor: sağlayıcı
   * değişince kanonik geçmişin de değişmesi gerekirdi.
   *
   * ⚠ ZARF ile PAYLOAD iş bölümü, belgenin *"her olay idempotent kimlik, aktör/sistem
   * kaynağı, hedef abonelik, tenant, önceki-yeni durum ve correlation kimliği
   * taşımalıdır"* cümlesinin karşılığıdır: idempotent kimlik (`event.id`), aktör, tenant
   * ve correlation ZARFTA (`buildEvent`); payload'ın taşıdığı yalnız **hedef abonelik**
   * ve **önceki-yeni durum**. İkisini payload'a da yazmak iki gerçek kaynağı üretirdi.
   *
   * ⚠ PARA YOK: tutar, para birimi, kart ve fatura alanı hiçbir payload'da yok. Bir
   * tenant'ın ne ödediği, olayı tüketen herkese açılmamalı; tüketici rakamı okuma
   * yolundan yetkiyle alıyor (`contract.date_changed` ile aynı karar).
   *
   * ⚠ `from`/`to` her olayda O OLAYIN ÖZNESİNİN durumudur — ödeme olaylarında ödeme
   * denemesinin, abonelik olaylarında aboneliğin, plan değişiminde plan anahtarının.
   * Geçişi OLMAYAN olaylarda (eşik sinyalleri) `from`/`to` uydurulmaz: olmayan bir
   * geçişi kayda geçirmek, olmamış bir şeyi olmuş göstermek olurdu.
   *
   * ⚠ Bugün hiçbir üretici bu olayları yayınlamıyor ve bu bilinçli. Belge: *"Ticari olay
   * ailesi ödeme sağlayıcısı ve CLOS-201/202/203 temel faturalama akışları
   * TAMAMLANDIKTAN SONRA uygulanacaktır."* Bu kart sözleşmeyi ve SIRA KURALINI
   * (`commerceOrder.ts`) kuruyor.
   */
  "subscription.trial_ending": {
    name: "subscription.trial_ending",
    version: 1,
    producer: "subscription",
    // Eşiği ZAMAN geçirdi; düğmeye basan yok. Aktör uydurulmaz (CLOS-318).
    actor: "system",
    sensitivity: "confidential",
    summary: "Deneme süresinin bitişi yaklaştı.",
    /** ⚠ `from`/`to` YOK: deneme hâlâ sürüyor, henüz bir geçiş olmadı — bu bir EŞİK sinyali. */
    payload: z.strictObject({ subscriptionId: z.uuid(), thresholdKey: z.string().min(1) }),
  },
  "subscription.trial_ended": {
    name: "subscription.trial_ended",
    version: 1,
    producer: "subscription",
    actor: "system",
    sensitivity: "confidential",
    summary: "Deneme süresi sona erdi.",
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
    }),
  },
  /**
   * ⚠ Ödeme olayları `payment.*` altında ve abonelikten AYRI: bir ödeme ile bir abonelik
   * geçişi farklı gerçeklerdir. Aynı ada sıkıştırsaydık belgenin *"tek PaymentSucceeded →
   * tek abonelik geçişi"* iddiası ölçülemez olurdu — ikisi tek satıra düşerdi.
   */
  "payment.succeeded": {
    name: "payment.succeeded",
    version: 1,
    producer: "billing",
    // Doğrulamayı sağlayıcı callback'i getiriyor; ödemeyi başlatan kişi onu ONAYLAMIYOR.
    actor: "system",
    sensitivity: "confidential",
    summary: "Ödeme başarıyla tamamlandı.",
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      paymentId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
    }),
  },
  "payment.failed": {
    name: "payment.failed",
    version: 1,
    producer: "billing",
    actor: "system",
    sensitivity: "confidential",
    summary: "Ödeme başarısız oldu.",
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      paymentId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
      /** ⚠ KOD, serbest metin değil: sağlayıcının hata cümlesi kullanıcıya gösterilecek metin değildir (CLOS-217). */
      reasonCode: z.string().min(1),
    }),
  },
  /**
   * ⚠ Yenileme, ilk ödemeden AYRI ad taşıyor ve bu semantik ayrım belgede korunması
   * istenen ayrımlardan biri: ilk ödeme bir aboneliği DOĞURUR, yenileme onu SÜRDÜRÜR.
   * Tek ada sıkıştırsaydık "ilk mi, kaçıncı mı?" sorusu olayın içinde belirsizleşirdi.
   */
  "subscription.renewal_succeeded": {
    name: "subscription.renewal_succeeded",
    version: 1,
    producer: "subscription",
    actor: "system",
    sensitivity: "confidential",
    summary: "Abonelik yenilemesi başarıyla tamamlandı.",
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      paymentId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
    }),
  },
  "subscription.renewal_failed": {
    name: "subscription.renewal_failed",
    version: 1,
    producer: "subscription",
    actor: "system",
    sensitivity: "confidential",
    summary: "Abonelik yenilemesi başarısız oldu.",
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      paymentId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
      reasonCode: z.string().min(1),
    }),
  },
  /**
   * ABONELİĞİN AÇILDIĞI AN (CLOS-884 · alt görev CLOS-1075).
   *
   * ### ⚠ AİLE ADI ZATEN KARARLIYDI, bu olay onu izliyor
   *
   * Kart *"ticari event family adlandırma kararı"* istiyordu; karar fiilen verilmişti:
   * ailenin on yedi üyesi (`subscription.activated`, `plan_changed`, `cancelled`…) çoktan
   * `subscription.*` altında yaşıyor ve `billing.*` diye ikinci bir aile hiç doğmadı.
   * Yeni bir ad uydurmak, aynı kavramı iki ada bölmek olurdu. Bu satır kararı BOZMUYOR,
   * ailenin eksik çekirdeğini tamamlıyor.
   *
   * ### ⚠ `activated`'DAN FARKLI BİR AN
   *
   * `subscription.activated` DOĞRULANMIŞ ÖDEMENİN sonucu — deneme biten ya da ödemesi
   * onaylanan bir aboneliğin etkinleşmesi. Bu olay ise aboneliğin VAR OLMAYA BAŞLADIĞI
   * an: çalışma alanı açılıyor ve ona bir paket bağlanıyor. İkisini tek olaya indirseydik
   * "alan kuruldu" ile "para alındı" aynı satıra düşer ve geçmişte ayırt edilemezdi.
   *
   * ### ⚠ AKTÖR `user` — ÖLÇÜLDÜ, varsayılmadı
   *
   * İlk yazımda "kişisel alan sistemce doğuyor" diye düşünüp aktörü belirsiz bırakmıştım.
   * Üç kuruluş yolunun üçü de `startSubscription`'a GERÇEK bir kullanıcı kimliği veriyor:
   * organizasyon kuran kişi (`modules/tenants/service.ts`), hesabını açan kişi
   * (`personalWorkspace.ts`) ve dönüşümü başlatan kişi (`sponsoredConversion.ts`).
   * Sistem aktörü yazsaydık, geçmişte "bu aboneliği kim açtı" sorusu cevapsız kalırdı —
   * oysa cevap her seferinde elimizde.
   *
   * ⚠ `subscription.activated`'ın aktörü SİSTEM ve bu bir tutarsızlık değil: orada
   * kullanıcı bir düğmeye basmıyor, doğrulanmış ödeme tetikliyor.
   *
   * ### ⚠ Yük GÜVENLİ: fiyat yok, rakam yok
   *
   * `activated` ile aynı ilke: plan ANAHTARI ürünün kamuya açık kataloğunun parçası,
   * ödenen tutar değil. `status` başlangıç durumunu taşıyor (`trialing` ya da `active`)
   * çünkü ikisi kullanıcı için farklı şeyler ve hangisiyle başlandığı sonradan
   * çıkarılamaz.
   */
  "subscription.started": {
    name: "subscription.started",
    version: 1,
    producer: "subscription",
    actor: "user",
    sensitivity: "confidential",
    summary: "Çalışma alanına abonelik açıldı.",
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      planKey: z.string().min(1),
      /** Başlangıç durumu: paketin deneme süresi varsa `trialing`, yoksa `active`. */
      status: z.string().min(1),
    }),
  },
  "subscription.activated": {
    name: "subscription.activated",
    version: 1,
    producer: "subscription",
    // Aktivasyon DOĞRULANMIŞ ödemenin sonucudur; kullanıcı "aktif et" düğmesine basmıyor.
    actor: "system",
    sensitivity: "confidential",
    summary: "Abonelik etkinleşti.",
    /** ⚠ Plan ANAHTARI var, fiyat yok: anahtar ürünün kamuya açık kataloğu, rakam değil. */
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      planKey: z.string().min(1),
      from: z.string().min(1),
      to: z.string().min(1),
    }),
  },
  "subscription.plan_changed": {
    name: "subscription.plan_changed",
    version: 1,
    producer: "subscription",
    // Planı SEÇEN bir kişidir; burada sistem aktörü yazmak, kararı sahipsiz bırakırdı.
    actor: "user",
    sensitivity: "confidential",
    summary: "Abonelik planı değişti.",
    /** ⚠ Burada `from`/`to` PLAN ANAHTARLARIDIR: değişen durum plandır, abonelik durumu değil. */
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
    }),
  },
  /**
   * ⚠ Planlanan iptal ile gerçekleşen iptal AYRI olaylardır ve bu belgenin koruduğu
   * semantik ayrımlardan biri: biri kullanıcının BUGÜN verdiği karar, diğeri o kararın
   * dönem sonunda doğurduğu SONUÇ. Tek olay olsaydı "aboneliğim şu an kapalı mı?" sorusu
   * cevapsız kalırdı.
   */
  "subscription.cancellation_scheduled": {
    name: "subscription.cancellation_scheduled",
    version: 1,
    producer: "subscription",
    actor: "user",
    sensitivity: "confidential",
    summary: "Abonelik iptali dönem sonuna planlandı.",
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
    }),
  },
  "subscription.cancelled": {
    name: "subscription.cancelled",
    version: 1,
    producer: "subscription",
    // Dönem sonunun gelmesine kimse karar vermiyor (`contract.signature_request_expired` deseni).
    actor: "system",
    sensitivity: "confidential",
    summary: "Abonelik sona erdi.",
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
      reasonCode: z.string().min(1),
    }),
  },
  /**
   * ⚠ Ödemesizlik zinciri İKİ AYRI olaydır (belge EVENT-AT-03: *"grace/restricted
   * kuralları DOĞRU SIRAYLA çalışmalı"*). Tek olayda birleştirilseydi "kullanıcının
   * düzeltme penceresi açıldı" ile "ürün kısıtlandı" ayırt edilemez, sıra kuralı da
   * ölçülemez olurdu.
   */
  "subscription.grace_period_started": {
    name: "subscription.grace_period_started",
    version: 1,
    producer: "subscription",
    actor: "system",
    sensitivity: "confidential",
    summary: "Ödeme alınamadı; ek süre başladı.",
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
      reasonCode: z.string().min(1),
    }),
  },
  "subscription.restricted_mode_started": {
    name: "subscription.restricted_mode_started",
    version: 1,
    producer: "subscription",
    actor: "system",
    sensitivity: "confidential",
    /**
     * ⚠ Kısıtlı mod bir ERİŞİM durumudur, bir SİLME değil (belge: *"kullanıcı verisi
     * silinmemeli"*). Katalogda silme anlamına gelen bir ticari olay yok ve olmadığı için
     * hiçbir tüketici onu tetikleyemez.
     */
    summary: "Ek süre doldu; hesap kısıtlı moda geçti.",
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      from: z.string().min(1),
      to: z.string().min(1),
    }),
  },
  /**
   * ⚠ Hak (entitlement) olayları `operational`: taşıdıkları şey bir SAYAÇ durumu, bir
   * ticari rakam değil. Ödeme ve abonelik olayları ise `confidential` — bir firmanın
   * ödeme geçmişi ticari bilgidir.
   */
  "entitlement.increased": {
    name: "entitlement.increased",
    version: 1,
    producer: "billing",
    actor: "system",
    sensitivity: "operational",
    summary: "Kullanım hakkı artırıldı.",
    /** ⚠ `from`/`to` yerine MİKTAR: burada değişen bir durum etiketi değil, bir sayaçtır. */
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      entitlementKey: z.string().min(1),
      fromQuantity: z.number().int().nonnegative(),
      toQuantity: z.number().int().nonnegative(),
    }),
  },
  "entitlement.exhausted": {
    name: "entitlement.exhausted",
    version: 1,
    producer: "billing",
    actor: "system",
    sensitivity: "operational",
    summary: "Kullanım hakkı tükendi.",
    /** ⚠ EŞİK sinyali: geçiş yok, sayaç sıfıra dokundu. */
    payload: z.strictObject({
      subscriptionId: z.uuid(),
      entitlementKey: z.string().min(1),
      thresholdKey: z.string().min(1),
    }),
  },
  /**
   * ⚠ AI kredisi eşikleri İKİ AYRI olay: "azaldı" bir uyarı, "bitti" bir kapıdır ve
   * tüketicileri farklı tepki verir. Tek olayda `level` alanıyla birleştirseydik, sıra
   * kuralı ("önce low, sonra exhausted") payload alanına gömülür ve ölçülemezdi.
   */
  "ai.credit_low": {
    name: "ai.credit_low",
    version: 1,
    producer: "aiCredits",
    actor: "system",
    sensitivity: "operational",
    summary: "AI kredisi azaldı.",
    /**
     * ⚠ BAKİYE DEĞERİ YOK, eşik ANAHTARI var (belge EVENT-AT-04: *"başka bağlama metadata
     * sızmamalı"*). Kalan kredi rakamı okuma yolundan yetkiyle alınır; ayrıca değer
     * taşınsaydı olay yayıldıktan sonra bayatlardı (`contract.date_reminder_due` dersi).
     */
    payload: z.strictObject({ subscriptionId: z.uuid(), thresholdKey: z.string().min(1) }),
  },
  "ai.credit_exhausted": {
    name: "ai.credit_exhausted",
    version: 1,
    producer: "aiCredits",
    actor: "system",
    sensitivity: "operational",
    summary: "AI kredisi tükendi.",
    payload: z.strictObject({ subscriptionId: z.uuid(), thresholdKey: z.string().min(1) }),
  },
  /**
   * ### Sözleşme AI analizi (KARAR 07 · KARAR 11)
   *
   * ⚠ AKTÖR `user`, `system` DEĞİL. Katalogun kendi kuralı: *"**AI yoktur** — AI aktör
   * değil kaynaktır (CLOS-318)."* Analizi bir kullanıcı İSTİYOR; model bir araç. Sistem
   * aktörü yazsaydık aktivite akışında "sistem sözleşmeyi analiz etti" görünür ve kimin
   * krediyi harcadığı kaybolurdu — oysa kredi tam olarak o kullanıcının bağlamına
   * yazılıyor (KARAR 04).
   *
   * ⚠ Payload ÇIKTIYI TAŞIMIYOR, yalnız analizin kimliğini. Olay, sözleşmeyi görme
   * yetkisi olmayan tüketicilere de gidiyor; analiz ise sözleşmenin özeti — yani
   * sözleşmenin kendisi kadar gizli. `contract.content_saved` ile birebir aynı karar.
   *
   * ⚠ İki AYRI olay (başarılı/başarısız), tek olay + `status` alanı değil: tüketicileri
   * farklı. Başarısızlık kullanıcının bilmesi gereken bir şey (kredisi iade edildi),
   * başarı ise akışa düşen bir kayıt. Tek olayda birleştirseydik yönlendirme kararı
   * payload alanına gömülür ve matriste ölçülemezdi.
   */
  "ai.analysis_completed": {
    name: "ai.analysis_completed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "Sözleşme için AI analizi tamamlandı.",
    payload: z.strictObject({
      analysisId: z.uuid(),
      contractId: z.uuid(),
      /** ⚠ DÜŞEN kredi: aktivite satırı "bu ne kadara mal oldu" sorusunu cevaplayabilmeli. */
      credits: z.number().int().min(0),
    }),
  },
  "ai.analysis_failed": {
    name: "ai.analysis_failed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "Sözleşme AI analizi başarısız oldu.",
    payload: z.strictObject({
      analysisId: z.uuid(),
      contractId: z.uuid(),
      /**
       * ⚠ Sebep KODU, serbest metin değil: sağlayıcının hata gövdesi gönderdiğimiz
       * sözleşme metnini yankılayabilir ve olay payload'ı sözleşmeyi görme yetkisi
       * olmayan tüketicilere de gider.
       */
      errorCode: z.enum(["provider_error", "invalid_output"]),
    }),
  },
  /**
   * ⚠ ÜRETİMDEN AYRI OLAY (dilim 2 kök nedeni 2 · CLOS-205 kriter 9-10). `ai.analysis_completed`
   * TASLAK üretimini anlatıyor — kredi orada düşüyor. Bu olay kullanıcının o taslağı
   * kalıcı geçmişe AÇIKÇA taşıdığı anı anlatıyor ve krediyle ilgisi yok. İkisini tek
   * olayda birleştirseydik "üretildi" ile "kullanıcı kabul etti" arasındaki KARAR 11
   * ayrımı (çıktı bir öneridir, kabul insanın eylemidir) olay düzeyinde kaybolurdu.
   */
  "ai.analysis_saved": {
    name: "ai.analysis_saved",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "AI analizi kalıcı geçmişe kaydedildi.",
    payload: z.strictObject({
      analysisId: z.uuid(),
      contractId: z.uuid(),
    }),
  },
  /**
   * ### AI sözleşme taslağı (KARAR 07 dilim 2 · CLOS-239 · CLOS-994)
   *
   * ⚠ AYNI ÜÇ KARAR `ai.analysis_*` ile: aktör `user` (AI aktör değil kaynak — CLOS-318),
   * payload İÇERİK TAŞIMAZ (üretilen taslak metni sözleşme kadar gizli), üretim/kabul AYRI
   * olay (KARAR 11: çıktı öneridir, kabul insanın eylemidir).
   *
   * ⚠ `ai.draft_accepted` TEK: `contract_id` burada VAR — `ai.analysis_saved`'in aksine,
   * kabul anında ARTIK gerçek bir sözleşme kimliği doğmuş oluyor ve olay onu taşıyor;
   * kabulden ÖNCE (taslak henüz üretilirken) böyle bir kimlik yok.
   */
  /**
   * ### REVİZYON ÖNERİSİ ve FARK AÇIKLAMASI (CLOS-207 `[190]`)
   *
   * ⚠ Payload ÖNERİ METNİNİ TAŞIMIYOR: önerilen metin sözleşmenin bir maddesinin yeni
   * hâli — sözleşmenin kendisi kadar gizli (`ai.analysis_*` ile aynı karar).
   *
   * ⚠ `ai.revision_saved` AYRI OLAY: üretim geçici bir öneridir, kaydetme kanonik bir
   * revizyon doğurur ve o revizyonun kimliği akışta görünmeli. Tek olayda birleştirseydik
   * "AI önerdi" ile "kullanıcı bunu resmî öneriye çevirdi" aynı satıra düşerdi.
   */
  "ai.revision_suggested": {
    name: "ai.revision_suggested",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "AI revizyon önerisi üretildi.",
    payload: z.strictObject({
      runId: z.uuid(),
      contractId: z.uuid(),
      credits: z.number().int().min(0),
    }),
  },
  "ai.compare_explained": {
    name: "ai.compare_explained",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "AI versiyon farkı açıklaması üretildi.",
    payload: z.strictObject({
      runId: z.uuid(),
      contractId: z.uuid(),
      credits: z.number().int().min(0),
    }),
  },
  "ai.revision_saved": {
    name: "ai.revision_saved",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "AI önerisi kanonik revizyon önerisine dönüştürüldü.",
    payload: z.strictObject({
      runId: z.uuid(),
      contractId: z.uuid(),
      revisionId: z.uuid(),
    }),
  },
  /**
   * ### RİSK/EKSİK HÜKÜM TARAMASI (CLOS-206 `[189]`)
   *
   * ⚠ `ai.analysis_*` ile AYNI ÜÇ KARAR: aktör `user` (AI aktör değil), payload BULGU
   * İÇERİĞİ TAŞIMAZ (bulgu metni sözleşmenin kendisinden alıntı taşıyor — sözleşme kadar
   * gizli), üretim ve KAYDETME ayrı olaylar.
   *
   * ⚠ `findingCount` bir SAYI, içerik değil: "bu tarama ne buldu" sorusunun akışta
   * cevaplanabilir en zararsız hâli. Kategorileri dökseydik, sözleşmede çelişkili hüküm
   * bulunduğu bilgisi kaydı göremeyen kiracı üyelerine sızardı.
   */
  "ai.risk_scan_completed": {
    name: "ai.risk_scan_completed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "Sözleşme için AI risk taraması tamamlandı.",
    payload: z.strictObject({
      riskRunId: z.uuid(),
      contractId: z.uuid(),
      credits: z.number().int().min(0),
      findingCount: z.number().int().min(0),
    }),
  },
  "ai.risk_scan_failed": {
    name: "ai.risk_scan_failed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "Sözleşme için AI risk taraması tamamlanamadı.",
    payload: z.strictObject({
      riskRunId: z.uuid(),
      contractId: z.uuid(),
      errorCode: z.enum(["provider_error", "invalid_output"]),
    }),
  },
  "ai.risk_scan_saved": {
    name: "ai.risk_scan_saved",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "AI risk bulguları kalıcı geçmişe kaydedildi.",
    payload: z.strictObject({
      riskRunId: z.uuid(),
      contractId: z.uuid(),
      findingCount: z.number().int().min(0),
    }),
  },
  "ai.draft_completed": {
    name: "ai.draft_completed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "AI sözleşme taslağı üretildi.",
    payload: z.strictObject({
      draftId: z.uuid(),
      credits: z.number().int().min(0),
    }),
  },
  "ai.draft_failed": {
    name: "ai.draft_failed",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "AI sözleşme taslağı üretilemedi.",
    payload: z.strictObject({
      draftId: z.uuid(),
      errorCode: z.enum(["provider_error", "invalid_output"]),
    }),
  },
  "ai.draft_accepted": {
    name: "ai.draft_accepted",
    version: 1,
    producer: "contracts",
    actor: "user",
    sensitivity: "confidential",
    summary: "AI sözleşme taslağı sözleşmeye dönüştürüldü.",
    payload: z.strictObject({
      draftId: z.uuid(),
      contractId: z.uuid(),
    }),
  },
  /**
   * ⚠ Sponsorlu Müvekkil Alanı kapasitesi `client.*` ailesinde kalıyor: alanın kendisi
   * `client.workspace_created` ile doğuyor ve kapasitenin dolması aynı ailenin gerçeği.
   * `subscription.*` altına koysaydık, aynı kavram iki ailede yaşardı.
   *
   * ⚠ Payload MÜVEKKİL KİMLİĞİ taşımıyor: tükenen şey tenant seviyesindeki kotadır, bir
   * müvekkile ait bir şey değil — kimlik yazmak, ilgisiz bir kaydı olaya bağlardı.
   */
  "client.workspace_capacity_exhausted": {
    name: "client.workspace_capacity_exhausted",
    version: 1,
    producer: "clients",
    actor: "system",
    sensitivity: "operational",
    summary: "Sponsorlu Müvekkil Alanı kapasitesi tükendi.",
    payload: z.strictObject({ subscriptionId: z.uuid(), thresholdKey: z.string().min(1) }),
  },
} as const satisfies Record<string, EventDefinition>;

export type DomainEventName = keyof typeof EVENT_CATALOG;

export type EventPayload<N extends DomainEventName> = z.infer<(typeof EVENT_CATALOG)[N]["payload"]>;

/** Ad katalogda var mı — çalışma anında gelen (kuyruk, HTTP) adlar için. */
export function isDomainEventName(value: unknown): value is DomainEventName {
  return typeof value === "string" && value in EVENT_CATALOG;
}

/**
 * Aktivite satırlarının Türkçe ÖZETLERİ (CLOS-283 `[055]`).
 *
 * ⚠ Metin veritabanında DEĞİL burada: `activity_entries.summary_key` yalnız anahtarı
 * saklıyor. Cümle satıra yazılsaydı bir metin düzeltmesi migration gerektirir ve geçmiş
 * satırlar eski ifadeyle kalırdı — aynı olay iki farklı cümleyle görünürdü.
 *
 * Fonksiyon, parametreleri cümleye yerleştiriyor. Bilinmeyen anahtar HAM dönüyor: boş
 * satır basmak, olmuş bir olayı olmamış gibi göstermek olurdu.
 */
export function activitySummary(key: string, params: Record<string, unknown> = {}): string {
  const p = params as Record<string, string | number | string[] | undefined>;
  switch (key) {
    case "tenant.created":
      return "Organizasyon oluşturuldu";
    case "membership.created":
      return "Organizasyona üye eklendi";
    case "organization.profile_updated":
      return `Organizasyon profili güncellendi (${Array.isArray(p.fields) ? p.fields.length : 0} alan)`;
    case "organization.closed":
      return "Organizasyon kapatıldı";
    case "organization.reactivated":
      return "Organizasyon yeniden etkinleştirildi";
    case "membership.status_changed":
      return `Üyelik durumu değişti: ${String(p.from ?? "")} → ${String(p.to ?? "")}`;
    case "membership.role_changed":
      return "Üyeliğin rolü değiştirildi";
    case "invitation.created":
      return "Davet gönderildi";
    case "invitation.resolved":
      return `Davet sonuçlandı: ${INVITATION_OUTCOME_NAMES[String(p.outcome ?? "")] ?? String(p.outcome ?? "")}`;
    case "department.created":
      return `Departman oluşturuldu: ${String(p.name ?? "")}`;
    case "department.archived":
      return "Departman arşivlendi";
    case "department.reactivated":
      return "Departman yeniden aktifleştirildi";
    case "role.created":
      return `Özel rol oluşturuldu: ${String(p.name ?? "")}`;
    case "role.permissions_changed":
      return `Rol yetkileri değişti (+${Number(p.added ?? 0)} / −${Number(p.removed ?? 0)})`;
    case "role.archived":
      return "Özel rol arşivlendi";
    case "role.reactivated":
      return "Özel rol yeniden aktifleştirildi";
    case "contract.responsibility_changed":
      return p.action === "removed"
        ? "Sözleşme sorumluluğu kaldırıldı"
        : "Sözleşme sorumluluğu atandı";
    case "contract.date_changed":
      return p.action === "removed" ? "Sözleşme tarihi kaldırıldı" : "Sözleşme tarihi güncellendi";
    case "contract.obligation_changed":
      if (p.action === "suggestion_accepted") return "AI yükümlülük önerisi kabul edildi";
      return p.action === "created"
        ? "Sözleşme yükümlülüğü oluşturuldu"
        : "Sözleşme yükümlülüğü güncellendi";
    case "contract.obligation_status_changed":
      return `Yükümlülük durumu değişti: ${String(p.from ?? "")} → ${String(p.to ?? "")}`;
    case "contract.counterparty_revision_uploaded":
      return "Karşı taraftan gelen revizyon yüklendi";
    case "contract.service_assignment_activated":
      return "Sözleşmeye hukuki hizmet sağlayıcı atandı";
    case "contract.org_invited":
      return "Sözleşmeye haricî organizasyon daveti gönderildi";
    case "contract.org_invitation_resolved":
      return `Organizasyon daveti ${INVITATION_OUTCOME_NAMES[String(p.outcome)] ?? "sonuçlandı"}`;
    case "contract.participant_invited":
      return "Sözleşmeye haricî katılımcı daveti gönderildi";
    case "contract.participant_invitation_resolved":
      return `Haricî katılımcı daveti ${INVITATION_OUTCOME_NAMES[String(p.outcome)] ?? "sonuçlandı"}`;
    case "contract.content_saved":
      return "Sözleşme metni güncellendi";
    case "contract.status_changed":
      return `Sözleşme durumu değişti: ${String(p.from ?? "")} → ${String(p.to ?? "")}`;
    case "record.access_granted":
      return "Kayda erişim verildi";
    case "record.access_closed":
      return "Kayda erişim sona erdi";
    case "file.scan_clean":
      return "Dosya taraması temiz";
    case "file.scan_infected":
      return "Dosya taraması zararlı buldu";
    case "client.updated":
      return "Müvekkil bilgileri güncellendi";
    case "client.merged":
      return "Müvekkil başka bir kayda birleştirildi";
    case "client.workspace_created":
      return "Sponsorlu Müvekkil Alanı kuruldu";
    case "client.contact_changed":
      return (
        CLIENT_CONTACT_SUMMARIES[String(p.action ?? "")] ?? "Müvekkilin iletişim kişileri değişti"
      );
    case "client.contract_linked":
      return "Sözleşme müvekkil portföyüne bağlandı";
    case "client.contract_unlinked":
      return "Sözleşmenin müvekkil portföyüyle bağı kaldırıldı";
    case "client.status_changed":
      return `Müvekkil durumu değişti: ${String(p.from ?? "")} → ${String(p.to ?? "")}`;
    case "client.representation_changed":
      return (
        CLIENT_REPRESENTATION_SUMMARIES[String(p.action ?? "")] ??
        "Müvekkil temsil ilişkisi değişti"
      );
    case "client.service_relationship_changed":
      return SERVICE_RELATIONSHIP_SUMMARIES[String(p.action ?? "")] ?? "Hizmet ilişkisi değişti";
    case "client.portfolio_assignment_changed":
      return (
        PORTFOLIO_ASSIGNMENT_SUMMARIES[String(p.action ?? "")] ??
        "Müvekkil portföy sorumluluğu değişti"
      );
    case "contract.comment_changed":
      return "Sözleşme yorumu değişti";
    case "contract.comment_mentioned":
      return "Yorumda bir kullanıcı anıldı";
    case "contract.work_item_changed":
      return "Sözleşme çalışma nesnesi değişti";
    case "contract.object_share_changed":
      return "Sözleşme nesnesi paylaşıldı";
    case "contract.signature_request_changed":
      if (p.action === "cancelled") return "Sözleşme imza talebi iptal edildi";
      if (p.action === "signers_set") return "Sözleşme imzacıları belirlendi";
      if (p.action === "signer_reassigned") return "Sözleşme imzacısı yeniden atandı";
      if (p.action === "sent") return "Sözleşme imza talebi gönderildi";
      return "Sözleşme için imza talebi oluşturuldu";
    case "contract.signer_status_changed":
    case "contract.signer_status_synced":
      if (p.action === "signed") return "Sözleşme imzalandı";
      if (p.action === "declined") return "Sözleşme imzası reddedildi";
      if (p.action === "cancelled") return "Sözleşme imzacısının görevi iptal edildi";
      if (p.action === "failed") return "Sözleşme imzasında hata oluştu";
      return "Sözleşme imzacı tarafından görüntülendi";
    case "contract.linked_contract_created":
      if (p.linkType === "amendment") return "Sözleşmeden tadil oluşturuldu";
      if (p.linkType === "renewal") return "Sözleşmeden yenileme oluşturuldu";
      return "Sözleşmeden ek protokol oluşturuldu";
    case "contract.manual_signature_recorded":
      return "Sözleşme için manuel imza kaydedildi";
    case "contract.signed_document_attached":
      return "Sözleşmenin imzalı belgesi arşivlendi";
    case "contract.signature_request_expired":
      return "Sözleşme imza talebinin süresi doldu";
    case "contract.signature_request_settled":
      if (p.action === "completed") return "Sözleşme imza süreci tamamlandı";
      if (p.action === "declined") return "Sözleşme imza süreci reddedilerek kapandı";
      return "Sözleşme imza süreci hatayla kapandı";
    case "contract.signature_request_superseded":
      return "İmza talebi yeni versiyon nedeniyle geçersizleşti";
    case "contract.reconciliation_superseded":
      return "Mutabakat akışı yeni versiyon nedeniyle geçersizleşti";
    case "contract.reconciliation_changed":
      return (
        CONTRACT_RECONCILIATION_SUMMARIES[String(p.action ?? "")] ?? "Sözleşme mutabakatı değişti"
      );
    case "contract.approval_superseded":
      return "Onay akışı yeni versiyon nedeniyle geçersizleşti";
    case "contract.approval_changed":
      return CONTRACT_APPROVAL_SUMMARIES[String(p.action ?? "")] ?? "Sözleşme onayı değişti";
    case "contract.review_reminder_due":
      return "İnceleme görevi için hatırlatma oluştu";
    case "contract.review_changed":
      return CONTRACT_REVIEW_SUMMARIES[String(p.action ?? "")] ?? "Sözleşme incelemesi değişti";
    case "contract.negotiation_changed":
      return "Sözleşme müzakeresi değişti";
    case "client.representative_invited":
      return "Müvekkil için temsilci daveti gönderildi";
    case "client.representative_invitation_resolved":
      return `Temsilci daveti ${INVITATION_OUTCOME_NAMES[String(p.outcome)] ?? "sonuçlandı"}`;
    /**
     * ⚠ CÜMLE "AI ÜRETTİ" DİYOR — KARAR 11'in aktivite ayağı: *"Aktivite kaydında 'AI
     * üretti' ayrı görünür."* "Sözleşme analiz edildi" deseydik akışta bir insanın
     * incelemesinden ayırt edilemezdi ve altı ay sonra kimse özetin kaynağını bilemezdi.
     */
    case "ai.analysis_completed":
      return `Sözleşme AI ile analiz edildi (AI üretti · ${Number(p.credits ?? 0)} kredi)`;
    case "ai.analysis_failed":
      return "Sözleşme AI analizi tamamlanamadı; kredi iade edildi";
    case "ai.analysis_saved":
      return "AI analizi kalıcı geçmişe kaydedildi";
    case "ai.revision_suggested":
      return `AI revizyon önerisi üretildi (${Number(p.credits ?? 0)} kredi)`;
    case "ai.compare_explained":
      return `AI versiyon farkı açıklaması üretildi (${Number(p.credits ?? 0)} kredi)`;
    case "ai.revision_saved":
      return "AI önerisi revizyon önerisine dönüştürüldü";
    case "ai.risk_scan_completed":
      return `AI risk taraması tamamlandı (${Number(p.findingCount ?? 0)} bulgu · ${Number(p.credits ?? 0)} kredi)`;
    case "ai.risk_scan_failed":
      return "AI risk taraması tamamlanamadı; kredi iade edildi";
    case "ai.risk_scan_saved":
      return `AI risk bulguları kaydedildi (${Number(p.findingCount ?? 0)} bulgu)`;
    case "ai.draft_completed":
      return `AI ile sözleşme taslağı üretildi (AI üretti · ${Number(p.credits ?? 0)} kredi)`;
    case "ai.draft_failed":
      return "AI sözleşme taslağı üretilemedi; kredi iade edildi";
    case "ai.draft_accepted":
      return "AI taslağı sözleşmeye dönüştürüldü";
    /**
     * ### Ticari olay ailesi (CLOS-286 `[181]`, alt görev CLOS-974)
     *
     * ⚠ Maskeli satırda bu cümleler HİÇ ÇAĞRILMIYOR: `query.ts` maskeliyken
     * `summaryParams`'ı `{}`'e indiriyor ve arayüz maskeli satır için kendi genel
     * başlığını kullanıyor (`[055]`'in maskeleme davranışıyla aynı desen).
     */
    case "subscription.started":
      return `Abonelik açıldı (${String(p.planKey ?? "")})`;
    case "subscription.trial_ending":
      return "Deneme süresinin bitişi yaklaşıyor";
    case "subscription.trial_ended":
      return "Deneme süresi sona erdi";
    case "payment.succeeded":
      return "Ödeme başarıyla tamamlandı";
    case "payment.failed":
      return "Ödeme başarısız oldu";
    case "subscription.renewal_succeeded":
      return "Abonelik yenilemesi tamamlandı";
    case "subscription.renewal_failed":
      return "Abonelik yenilemesi başarısız oldu";
    case "subscription.activated":
      return "Abonelik etkinleşti";
    case "subscription.plan_changed":
      return "Abonelik planı değişti";
    case "subscription.cancellation_scheduled":
      return "Abonelik iptali dönem sonuna planlandı";
    case "subscription.cancelled":
      return "Abonelik sona erdi";
    case "subscription.grace_period_started":
      return "Ödeme alınamadı; ek süre başladı";
    case "subscription.restricted_mode_started":
      return "Hesap Kısıtlı moda geçti";
    case "entitlement.increased":
      return "Kullanım hakkı artırıldı";
    case "entitlement.exhausted":
      return "Kullanım hakkı tükendi";
    case "ai.credit_low":
      return "AI kredisi azaldı";
    case "ai.credit_exhausted":
      return "AI kredisi tükendi";
    case "client.workspace_capacity_exhausted":
      return "Sponsorlu Müvekkil Alanı kapasitesi tükendi";
    default:
      return key;
  }
}

/**
 * Yaşam döngüsü EYLEMLERİNİN ayrı cümleleri (CLOS-285 `[147]`).
 *
 * ⚠ Olay adı değil EYLEM ayrıştırılıyor: `client.service_relationship_changed` tek bir
 * kanonik olaydır (katalog kilidi bozulmuyor) ama kullanıcı akışında talep, kabul, askı
 * ve son aynı cümleyle görünseydi geçmiş okunamazdı — arka arkaya dört "değişti" satırı.
 *
 * ⚠ Bilinmeyen eylem AİLE cümlesine düşüyor, ham anahtara değil: yeni bir eylem
 * eklendiğinde akışta bir olay adının kendisi görünmesi kullanıcıya iç ayrıntı
 * göstermek olurdu.
 */
const SERVICE_RELATIONSHIP_SUMMARIES: Record<string, string> = {
  requested: "Hizmet ilişkisi talep edildi",
  accepted: "Hizmet ilişkisi kabul edildi",
  rejected: "Hizmet ilişkisi talebi reddedildi",
  cancelled: "Hizmet ilişkisi talebi geri çekildi",
  suspended: "Hizmet ilişkisi askıya alındı",
  resumed: "Hizmet ilişkisi yeniden başlatıldı",
  ended: "Hizmet ilişkisi sona erdi",
};

const PORTFOLIO_ASSIGNMENT_SUMMARIES: Record<string, string> = {
  assigned: "Müvekkil portföy sorumlusu atandı",
  reassigned: "Müvekkil portföy sorumlusu değiştirildi",
  closed: "Müvekkil portföy sorumluluğu kapatıldı",
};

const CLIENT_CONTACT_SUMMARIES: Record<string, string> = {
  created: "Müvekkile iletişim kişisi eklendi",
  updated: "Müvekkilin iletişim kişisi güncellendi",
  deactivated: "Müvekkilin iletişim kişisi pasife alındı",
};

const CLIENT_REPRESENTATION_SUMMARIES: Record<string, string> = {
  activated: "Müvekkil temsil yetkisi tanımlandı",
  scope_changed: "Müvekkil temsil kapsamı güncellendi",
  suspended: "Müvekkil temsil yetkisi askıya alındı",
  reactivated: "Müvekkil temsil yetkisi yeniden başlatıldı",
  ended: "Müvekkil temsil yetkisi sona erdi",
};

/** Mutabakat akışının eylem cümleleri (CLOS-161 `[154]`) — karar halkaları `[155]`'te. */
const CONTRACT_RECONCILIATION_SUMMARIES: Record<string, string> = {
  started: "Taraflara mutabakat talebi gönderildi",
  cancelled: "Mutabakat talebi iptal edildi",
  party_agreed: "Bir taraf mutabakata katıldı",
  party_disagreed: "Bir taraf mutabakata katılmadı",
  party_changes_requested: "Bir taraf mutabakat için değişiklik istedi",
  agreed: "Bütün taraflar mutabakata vardı",
  disagreed: "Mutabakat sağlanamadı",
  changes_requested: "Mutabakat değişiklik talebiyle kapandı",
};

/** Onay akışının eylem cümleleri (CLOS-158 `[151]`) — karar halkaları `[152]`'de. */
const CONTRACT_APPROVAL_SUMMARIES: Record<string, string> = {
  started: "Sözleşme onaya gönderildi",
  cancelled: "Sözleşme onayı iptal edildi",
  task_approved: "Bir onaylayıcı sözleşmeyi onayladı",
  task_rejected: "Bir onaylayıcı sözleşmeyi reddetti",
  task_changes_requested: "Bir onaylayıcı onay için değişiklik istedi",
  task_cancelled: "Bir onay görevi iptal edildi",
  task_reassigned: "Bir onay görevi başka bir onaylayıcıya devredildi",
  stage_approved: "Onay aşaması tamamlandı ve sıra sonraki aşamaya geçti",
  approved: "Sözleşme onay akışı tamamlandı",
  rejected: "Sözleşme onayı reddedilerek kapandı",
  changes_requested: "Sözleşme onayı değişiklik talebiyle kapandı",
};

/** İnceleme akışının eylem cümleleri (CLOS-155 `[148]`) — sonuç halkaları `[150]`'de. */
const CONTRACT_REVIEW_SUMMARIES: Record<string, string> = {
  started: "Sözleşme incelemeye gönderildi",
  cancelled: "Sözleşme incelemesi iptal edildi",
  task_completed: "Bir inceleyici incelemesini tamamladı",
  task_changes_requested: "Bir inceleyici incelemede değişiklik istedi",
  task_cancelled: "Bir inceleme görevi iptal edildi",
  task_reassigned: "Bir inceleme görevi başka bir inceleyiciye devredildi",
  completed: "Sözleşme incelemesi tamamlandı",
  changes_requested: "Sözleşme incelemesi değişiklik talebiyle kapandı",
};

const INVITATION_OUTCOME_NAMES: Record<string, string> = {
  accepted: "kabul edildi",
  rejected: "reddedildi",
  cancelled: "iptal edildi",
  expired: "süresi doldu",
};
